import { uniq } from "lodash-es";
import type { InferType } from "yup";
import { array, boolean, object, string } from "yup";

import type { Accreditation } from "@thalamos/aspire-common";
import {
  formValidationErrorMessages,
  isoDateString,
  nonEmptyRequiredString,
  requiredPostalCode,
} from "@thalamos/aspire-common";

export const PROF_REG_TYPES = {
  GMC: "doctor",
  SWE: "social-worker",
  NMC: "nurse",
  HCPC: "hcpc",
} as const satisfies Record<string, Accreditation["type"]>;

export const APPROVAL_TYPES = {
  AC: "approved-clinician",
  S12: "section-12",
  AMHP: "amhp",
} as const satisfies Record<string, Accreditation["type"]>;

export const dataSchema = object({
  name: nonEmptyRequiredString
    .required("Please enter your name")
    .max(255, formValidationErrorMessages.tooManyCharactersError),
  address: string()
    .required("Please enter your address")
    .max(255, formValidationErrorMessages.tooManyCharactersError),
  postalCode: requiredPostalCode(),
  gmcNumber: string().test({
    name: "validator-gmcNumber",
    test: function (value, ctx) {
      return !value && ctx.parent.profRegistrations.includes(PROF_REG_TYPES.GMC)
        ? this.createError({
            message: "Please enter your GMC number",
            path: "gmcNumber",
          })
        : true;
    },
  }),
  occupation: array().default([]),
  teams: array().default([]),
  profRegistrations: array().default([]),
  mhApprovals: array().default([]),

  section12ExpiryDate: isoDateString
    .test({
      name: "validator-section12ExpiryDate",
      test: function (value, ctx) {
        const derived = deriveValues(ctx.parent);
        return !value && derived.s12ExpiryRequired
          ? this.createError({
              message: "Please enter Section 12 expiry date",
              path: "section12ExpiryDate",
            })
          : true;
      },
    })
    .test({
      name: "validator-section12ExpiryDate-not-past",
      test: function (value, ctx) {
        if (!value) return true;
        if (ctx.parent.section12SyncedWithApprovalsApi) return true;
        const derived = deriveValues(ctx.parent);
        if (derived.isRmp && derived.isApprovedClinician) return true;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const expiryDate = new Date(value);
        if (expiryDate < today) {
          return this.createError({
            message: "Section 12 expiry date cannot be in the past",
            path: "section12ExpiryDate",
          });
        }
        return true;
      },
    }),
  section12SyncedWithApprovalsApi: boolean().default(false),

  localAuthority: string()
    .nullable()
    .test({
      name: "validator-localAuthority",
      test: function (value, ctx) {
        const derived = deriveValues(ctx.parent);

        if (derived.isAmhp && !value) {
          return this.createError({
            message: "Please enter the local authority you are approved by",
            path: "localAuthority",
          });
        } else if (derived.isAmhp && !derived.canBeAmhp) {
          return this.createError({
            message:
              "You must have either a HCPC, Nurse, or Social Worker accreditation",
            path: "localAuthority",
          });
        } else {
          return true;
        }
      },
    }),
  localAuthorityOrganisationId: string(),

  socialWorkerNumber: string().when("profRegistrations", {
    is: (regs: Accreditation["type"][]) => regs.includes(PROF_REG_TYPES.SWE),
    then: () =>
      nonEmptyRequiredString.required(
        "Please enter your SWE registration number",
      ),
  }),

  hcpcNumber: string().when("profRegistrations", {
    is: (regs: Accreditation["type"][]) => regs.includes(PROF_REG_TYPES.HCPC),
    then: () =>
      nonEmptyRequiredString.required(
        "Please enter your HCPC registration number",
      ),
  }),

  pinNumber: string().when("profRegistrations", {
    is: (regs: Accreditation["type"][]) => regs.includes(PROF_REG_TYPES.NMC),
    then: () => nonEmptyRequiredString.required("Please enter your PIN number"),
  }),

  approvedClinicianExpiryDate: isoDateString.when("mhApprovals", {
    is: (apps: Accreditation["type"][]) => apps.includes(APPROVAL_TYPES.AC),
    then: () =>
      isoDateString
        .required("Please enter Approved Clinician expiry date")
        .test({
          name: "validator-approvedClinicianExpiryDate-not-past",
          test: function (value, ctx) {
            if (!value) return true;
            if (ctx.parent.approvedClinicianSyncedWithApprovalsApi) return true;
            const derived = deriveValues(ctx.parent);
            if (!derived.isRmp) return true;
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const expiryDate = new Date(value);
            if (expiryDate < today) {
              return this.createError({
                message: "Approved Clinician expiry date cannot be in the past",
                path: "approvedClinicianExpiryDate",
              });
            }
            return true;
          },
        }),
  }),
  approvedClinicianSyncedWithApprovalsApi: boolean().default(false),
  defaultSignature: string(),
});

interface DerivedValues {
  isRmp: boolean;
  isNurse: boolean;
  isHcpc: boolean;
  isSocialWorker: boolean;
  isAmhp: boolean;
  isApprovedClinician: boolean;
  isSection12Approved: boolean;
  canBeAmhp: boolean;
  s12ExpiryRequired: boolean;
}

export function deriveValues(
  profileData: InferType<typeof dataSchema>,
): DerivedValues {
  const isRmp = profileData.profRegistrations.includes(PROF_REG_TYPES.GMC);
  const isNurse = profileData.profRegistrations.includes(PROF_REG_TYPES.NMC);
  const isHcpc = profileData.profRegistrations.includes(PROF_REG_TYPES.HCPC);
  const isSocialWorker = profileData.profRegistrations.includes(
    PROF_REG_TYPES.SWE,
  );
  const isAmhp = profileData.mhApprovals.includes(APPROVAL_TYPES.AMHP);
  const isApprovedClinician = profileData.mhApprovals.includes(
    APPROVAL_TYPES.AC,
  );
  const isSection12Approved =
    (isRmp && isApprovedClinician) ||
    profileData.mhApprovals.includes(APPROVAL_TYPES.S12);

  return {
    isRmp,
    isNurse,
    isHcpc,
    isSocialWorker,
    isAmhp,
    isApprovedClinician,
    isSection12Approved,
    canBeAmhp: isNurse || isHcpc || isSocialWorker,
    s12ExpiryRequired: isSection12Approved && !(isRmp && isApprovedClinician),
  };
}

export function applyFormInvariants(
  values: InferType<typeof dataSchema>,
): InferType<typeof dataSchema> {
  const newValues = { ...values };
  const derived = deriveValues(newValues);

  // Unselect AMHP if none of NMC, HCPC, SWE are selected
  if (!derived.canBeAmhp) {
    newValues.mhApprovals = newValues.mhApprovals.filter(
      (v) => v !== APPROVAL_TYPES.AMHP,
    );
  }

  // Unselect S12 if GMC is not selected
  // Select S12 if GMC and AC are selected
  if (!derived.isRmp) {
    newValues.mhApprovals = newValues.mhApprovals.filter(
      (v) => v !== APPROVAL_TYPES.S12,
    );
  } else if (derived.isApprovedClinician) {
    newValues.mhApprovals = uniq([
      ...newValues.mhApprovals,
      APPROVAL_TYPES.S12,
    ]);
    // When AC is newly selected but has no date yet, seed from existing S12 date
    if (
      !newValues.approvedClinicianExpiryDate &&
      newValues.section12ExpiryDate
    ) {
      newValues.approvedClinicianExpiryDate = newValues.section12ExpiryDate;
      newValues.approvedClinicianSyncedWithApprovalsApi =
        newValues.section12SyncedWithApprovalsApi;
    }
    // Keep S12 expiry in sync with AC expiry — AC takes precedence
    newValues.section12ExpiryDate = newValues.approvedClinicianExpiryDate;
  }

  // Unselect all mental health approvals if no professional registrations are selected
  if (!values.profRegistrations.length) {
    newValues.mhApprovals = [];
  }

  return newValues;
}

export function createAccreditations(userData: InferType<typeof dataSchema>) {
  const derived = deriveValues(userData);

  const doctorAccred = { type: "doctor", gmcNumber: userData.gmcNumber! };
  const nurseAccred = { type: "nurse", pinNumber: userData.pinNumber! };
  const acAccred = {
    type: "approved-clinician",
    expiryDate: userData.approvedClinicianExpiryDate!,
    syncedWithApprovalsApi:
      userData.approvedClinicianSyncedWithApprovalsApi ?? false,
  };
  const swAccred = {
    type: "social-worker",
    registrationNumber: userData.socialWorkerNumber!,
  };
  const hcpcAccred = { type: "hcpc", registrationNumber: userData.hcpcNumber! };
  const amhpAccred = {
    type: "amhp",
    localAuthority: userData.localAuthority!,
    localAuthorityOrganisationId: userData.localAuthorityOrganisationId!,
  };
  const s12Accred = {
    type: "section-12",
    expiryDate:
      derived.isRmp && derived.isApprovedClinician
        ? userData.approvedClinicianExpiryDate!
        : userData.section12ExpiryDate!,
    syncedWithApprovalsApi:
      derived.isRmp && derived.isApprovedClinician
        ? (userData.approvedClinicianSyncedWithApprovalsApi ?? false)
        : (userData.section12SyncedWithApprovalsApi ?? false),
  };

  // Combine accreditations into an array
  const accreditations = [
    derived.isRmp && doctorAccred,
    derived.isNurse && nurseAccred,
    derived.isHcpc && hcpcAccred,
    derived.isSocialWorker && swAccred,
    derived.isAmhp && amhpAccred,
    derived.isApprovedClinician && acAccred,
    derived.isSection12Approved && s12Accred,
  ].filter(Boolean) as Accreditation[];

  return accreditations;
}
